// A tiny Zod-like validator utility tailored for the Edit Profile sections.
// Exports one main function: validateSections(sections)
// It returns { valid: boolean, errors: Array<{id, field, messages: string[]}> }

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const allowedDocTypes = ["image/png", "image/jpg", "image/jpeg"];

function validateSupportiveDocument(document, fileUrl) {
  // Allow if file URL exists (already uploaded) or if new file is provided
  if (fileUrl) return []; // File already exists
  if (!document) return ["Supportive document is required"];
  const msgs = [];
  if (document.size && document.size > MAX_FILE_SIZE) msgs.push("Document must be <= 2MB");
  if (document.type && !allowedDocTypes.includes(document.type)) msgs.push("Unsupported file type. Only PNG, JPG, and JPEG are allowed");
  return msgs;
}

function isEmptyString(v) {
  return v == null || String(v).trim() === "";
}

function validateName(values) {
  const msgs = [];
  const nameRegex = /^([a-zA-Z ]){2,30}$/;

  if (isEmptyString(values.first_name)) {
    msgs.push("First name is required");
  } else if (!nameRegex.test(values.first_name)) {
    msgs.push("First name should contain only alphabets and spaces (2-30 chars)");
  }

  if (!isEmptyString(values.middle_name) && !nameRegex.test(values.middle_name)) {
    msgs.push("Middle name should contain only alphabets and spaces (2-30 chars)");
  }

  if (!isEmptyString(values.last_name) && !nameRegex.test(values.last_name)) {
    msgs.push("Last name should contain only alphabets and spaces (2-30 chars)");
  }
  
  // Validate supportive document - check both file and fileUrl
  const docMsgs = validateSupportiveDocument(values.file || values.name_document, values.fileUrl);
  msgs.push(...docMsgs);
  
  return msgs;
}

function validateDob(values) {
  const msgs = [];
  if (isEmptyString(values.dob)) msgs.push("DOB is required");
  else {
    // accept DD-MM-YYYY or YYYY-MM-DD roughly
    const ddmmyyyy = /^\d{2}-\d{2}-\d{4}$/;
    const iso = /^\d{4}-\d{2}-\d{2}$/;
    if (!ddmmyyyy.test(values.dob) && !iso.test(values.dob)) {
      msgs.push("DOB should be in DD-MM-YYYY or YYYY-MM-DD format");
    } else {
      const date = new Date(values.dob);
      const today = new Date();
      if (date > today) {
        msgs.push("Date of birth cannot be in the future");
      }
    }
  }
  
  // Validate supportive document
  const docMsgs = validateSupportiveDocument(values.file || values.dob_document, values.fileUrl);
  msgs.push(...docMsgs);
  
  return msgs;
}

function validateAddress(values) {
  const msgs = [];
  if (isEmptyString(values.pincode)) msgs.push("Pincode is required");
  else if (!/^\d{6}$/.test(String(values.pincode))) msgs.push("Pincode must be a 6 digit number");
  
  // No document validation for address field
  
  return msgs;
}

function validateBank(values) {
  const msgs = [];
  if (isEmptyString(values.bank_name)) msgs.push("Bank name is required");
  if (isEmptyString(values.account_holder_name)) msgs.push("Account holder name is required");
  if (isEmptyString(values.account_number)) msgs.push("Account number is required");
  if (isEmptyString(values.branch_name)) msgs.push("Branch name is required");
  else if (!/^\d{6,20}$/.test(String(values.account_number)))
    msgs.push("Account number should be numeric (6-20 digits)");
  if (isEmptyString(values.ifsc_code)) msgs.push("IFSC code is required");
  // else if (!/^[A-Za-z]{4}\d{7}$/.test(String(values.ifsc_code))) msgs.push("IFSC should be like AAAA0000000");
  
  // Validate cancelled cheque or passbook document - check file and fileUrl
  const docMsgs = validateSupportiveDocument(
    values.file || values.cancelled_cheque_or_passbook, 
    values.fileUrl
  );
  msgs.push(...docMsgs);
  
  return msgs;
}

function validateProfile(section) {
  const msgs = [];
  const values = section.values || {};
  
  // supportive document for profile - check both file and fileUrl
  const supportive = values.file || values.profile_document || null;
  const docMsgs = validateSupportiveDocument(supportive, values.fileUrl);
  msgs.push(...docMsgs);

  return msgs;
}

function validateKitOrShoe(values, label) {
  const msgs = [];
  if (isEmptyString(values[label])) msgs.push(`${label.replace(/_/g, ' ')} is required`);
  
  // No document validation for kit_size and shoe_size fields
  
  return msgs;
}

function validateHeight(values) {
  const msgs = [];
  if (isEmptyString(values.height)) msgs.push("Height is required");
  
  // No document validation for height field
  
  return msgs;
}

function validateWeight(values) {
  const msgs = [];
  if (isEmptyString(values.weight)) msgs.push("Weight is required");
  
  // No document validation for weight field
  
  return msgs;
}

function validateBloodGroup(values) {
  const msgs = [];
  if (isEmptyString(values.blood_group)) msgs.push("Blood group is required");
  else {
    const validGroups = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
    if (!validGroups.includes(values.blood_group)) {
      msgs.push("Invalid blood group. Must be one of: A+, A-, B+, B-, AB+, AB-, O+, O-");
    }
  }
  
  // No document validation for blood_group field
  
  return msgs;
}

function validateIdentification(values) {
  const msgs = [];
  if (isEmptyString(values.identification_type)) {
    msgs.push("Identification type is required");
  }

  if (isEmptyString(values.identification_number)) {
    msgs.push("Identification number is required");
  } else {
    if (values.identification_type === "aadhaar") {
      // Aadhaar: 12 digits
      if (!/^\d{12}$/.test(values.identification_number)) {
        msgs.push("Aadhaar Number must be exactly 12 digits");
      }
    } else if (values.identification_type === "pan_card") {
      // PAN: 5 letters, 4 digits, 1 letter (e.g. ABCDE1234F)
      if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(values.identification_number)) {
        msgs.push("Invalid Pan Number format (e.g., ABCDE1234F)");
      }
    }
  }
  
  // Validate supportive document
  const docMsgs = validateSupportiveDocument(values.file || values.identification_document, values.fileUrl);
  msgs.push(...docMsgs);
  
  return msgs;
}

export function validateSections(sections) {
  // return shape: { valid: boolean, errorsById: { [id]: { field, messages: [] } } }
  const errorsById = {};
  sections.forEach((s) => {
    if (!s.field) return; // skip unselected
    const field = s.field;
    let msgs = [];
    switch (field) {
      case "name":
        msgs = validateName(s.values || {});
        break;
      case "dob":
        msgs = validateDob(s.values || {});
        break;
      case "address":
        msgs = validateAddress(s.values || {});
        break;
      case "photo":
        // legacy/alias: treat "photo" same as "profile"
        msgs = validateProfile(s);
        break;
      case "profile":
        msgs = validateProfile(s);
        break;
      case "kit_size":
        msgs = validateKitOrShoe(s.values || {}, "kit_size");
        break;
      case "shoe_size":
        msgs = validateKitOrShoe(s.values || {}, "shoe_size");
        break;
      case "bank_details":
        msgs = validateBank(s.values || {});
        break;
      case "height":
        msgs = validateHeight(s.values || {});
        break;
      case "weight":
        msgs = validateWeight(s.values || {});
        break;
      case "blood_group":
        msgs = validateBloodGroup(s.values || {});
        break;
      case "identification":
        msgs = validateIdentification(s.values || {});
        break;
      default:
        msgs = [];
    }
    if (msgs.length) errorsById[s.id] = { field: s.field, messages: msgs };
  });
  return { valid: Object.keys(errorsById).length === 0, errorsById };
}

export default { validateSections };